import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/UserContextProvider";
import { useApp } from "../context/AppContextProvider";
import {
  retrieveUserInformationFromToken,
  retrieveUserProfileListFromToken,
} from "../requests/ManagementRequests";
import { NotificationManager } from "../notifications/NotificationManager";
import i18n from "../language/i18n";
import { readExternalClientHandoffTokens } from "../utilization/ExternalClientHandoffUtilization";
import { isServerUnavailableError } from "../utilization/BackendMessageUtilization";

/**
 * Completes a login from a token pair the backend handed off in the URL.
 *
 * Every integration that provisions or authenticates a user elsewhere finishes the same way: read the pair
 * out of the URL, strip it, load the user, and hand the app its session. That tail was duplicated per
 * integration, so each new one arrived as another near-copy — and the copies had already drifted (the order
 * of the two token reads differs between this app and admin-web). Extracting it per app keeps the drift from
 * spreading without pretending the two apps' versions are interchangeable.
 *
 * Deliberately NOT shared with admin-web: that app checks `enabled` BEFORE fetching profiles, and unifying
 * the two would silently change one of them. Same reason this lives in the app rather than in `src/library`.
 */
export const useHandoffLogin = () => {
  const navigate = useNavigate();
  const { setBoarding } = useApp();
  const { handleStateLogin } = useAuth();

  /**
   * @param hash          `location.hash`
   * @param search        `location.search`
   * @param onFailure     called with an i18n key when the hand-off cannot be completed. The caller owns the
   *                      failure UX because the reason differs per integration; nothing here navigates on
   *                      failure, so a caller that forgets to handle it cannot leave a silent spinner.
   * @returns whether a session was established
   */
  const completeHandoffLogin = async (
    hash: string | null | undefined,
    search: string | null | undefined,
    onFailure: (messageKey: string) => void
  ): Promise<boolean> => {
    // Fragment first, query as a one-release fallback — see ExternalClientHandoffUtilization for why the
    // backend moved the pair out of the query string.
    const { accessToken, refreshToken } = readExternalClientHandoffTokens(
      hash,
      search
    );
    // Strip it immediately either way: replaceState with the bare pathname drops both the fragment and the
    // query, so the credential does not sit in history or get carried in an outbound Referer.
    window.history.replaceState({}, document.title, window.location.pathname);
    if (!accessToken || !refreshToken) {
      onFailure("invalid_user_credentials_text");
      return false;
    }

    const [
      { data: userData, err: userErr },
      { data: userProfileDTOListData, err: userProfileListErr },
    ] = await Promise.all([
      retrieveUserInformationFromToken(accessToken),
      retrieveUserProfileListFromToken(accessToken),
    ]);
    // Checked before the credentials branch: during a deploy the API refuses connections for minutes, and
    // that used to read "invalid username or password" to an SSO pupil who has no password. The pair is
    // already stripped from the URL, so the only retry is from the partner portal — the message says so.
    if (
      isServerUnavailableError(userErr) ||
      isServerUnavailableError(userProfileListErr)
    ) {
      onFailure("sso_server_unavailable_text");
      return false;
    }
    // `|| !userData` is load-bearing, not defensive padding: a 2xx with no body yields
    // `err === undefined, data === undefined`, so an err-only guard falls through and the `userData.enabled`
    // read below throws inside an un-awaited async function. That rejection is unhandled, `onFailure` never
    // runs, and the spinner spins forever — the exact failure this hook exists to prevent.
    if (userErr || !userData) {
      onFailure("invalid_user_credentials_text");
      return false;
    }

    if (!(userData as any).enabled) {
      // A real state, not a failure: the account exists but has not been activated, and the app has a
      // dedicated screen for it. Reporting it through onFailure would send the user back to /login with a
      // credentials error, which is both wrong and unactionable.
      setBoarding("ACCOUNT_ACTIVATION_REQUIRED");
      return true;
    }

    handleStateLogin(
      accessToken,
      refreshToken,
      userData,
      userProfileDTOListData
    );
    navigate("/");
    setBoarding("PROFILE_SELECTION_REQUIRED");
    NotificationManager.success(i18n.t("login_successful_text"));
    return true;
  };

  /**
   * Finish an SSO hand-off — in-page when the backend's URL points back at THIS document, by navigation otherwise.
   *
   * <b>Assigning `window.location.href` is not enough, and for the common case it is actively broken.</b> The
   * hand-off URL is `<console base>/login/with-<provider>#accessToken=…&refreshToken=…`, and the SSO screen is
   * already sitting on exactly that path (it stripped its own query before the exchange). When the resolved role
   * belongs in THIS console the two differ only in the fragment, so the assignment is a same-document fragment
   * navigation: no reload, only `hashchange` — which nothing listens for, and the screen's dispatch effect has an
   * empty dep array and never re-runs. Spinner forever, with the token pair stranded in the address bar until
   * the user refreshes (which is exactly what "it logs in only after a refresh" looked like on SEBIT).
   *
   * Completing in-page also means the pair never round-trips through the URL on that path. The cross-console case
   * (a role resolved to the OTHER frontend) is a real navigation and still needs the URL — the backend, not this
   * page, decides which console a profile belongs in.
   *
   * @param redirectUrl          the backend's hand-off URL
   * @param onFailure            as for `completeHandoffLogin`
   * @param invalidUrlMessageKey i18n key reported when `redirectUrl` cannot be parsed
   */
  const completeHandoffOrNavigate = (
    redirectUrl: string,
    onFailure: (messageKey: string) => void,
    invalidUrlMessageKey: string
  ) => {
    let handoffUrl: URL;
    try {
      handoffUrl = new URL(redirectUrl, window.location.origin);
    } catch (error) {
      onFailure(invalidUrlMessageKey);
      return;
    }
    if (
      handoffUrl.origin === window.location.origin &&
      handoffUrl.pathname === window.location.pathname
    ) {
      completeHandoffLogin(handoffUrl.hash, "", onFailure);
      return;
    }
    window.location.href = redirectUrl;
  };

  return { completeHandoffLogin, completeHandoffOrNavigate };
};
